import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FamilyEvent } from './useOptimizedEvents';
import { FamilyTask } from './useOptimizedTasks';

interface DashboardData {
  events: FamilyEvent[];
  tasks: FamilyTask[];
  task_lists: any[];
  persons: any[];
}

/**
 * Hook optimisé qui charge toutes les données du dashboard en une seule requête RPC
 * Pré-remplit les caches individuels pour éviter les requêtes en cascade
 */
export function useDashboardData(familyId?: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['dashboard-data', familyId],
    queryFn: async (): Promise<DashboardData> => {
      if (!familyId) {
        return { events: [], tasks: [], task_lists: [], persons: [] };
      }

      const { data, error } = await supabase.rpc('get_dashboard_data', {
        p_family_id: familyId
      });

      if (error) {
        console.error('Error fetching dashboard data:', error);
        throw error;
      }

      const result = (data as unknown) as DashboardData;

      // Pré-remplir les caches individuels pour les autres hooks
      if (result.events) {
        queryClient.setQueryData(['optimized-events', familyId], result.events);
      }
      if (result.tasks) {
        queryClient.setQueryData(['optimized-tasks', familyId], result.tasks);
      }
      if (result.task_lists) {
        queryClient.setQueryData(['task-lists', familyId], result.task_lists);
      }
      if (result.persons) {
        queryClient.setQueryData(['optimized-person-profiles', familyId], result.persons);
      }

      return result;
    },
    enabled: !!familyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}
